import React, { useState } from 'react';
import Tooltip from '../../../components/ui/Tooltip';
import SectionToggle from './SectionToggle';
import type { LegNightTrain } from '../../journeys/api';
import {
  NIGHT_TRAIN_TOOLTIP, bedsHedge, bedsList, boardLine, fmtDay, getOffLine, productLine, runsLine, stationDistanceNote, timesLine, trustNote,
} from '../nightTrain';

// The night-train block of the leg sheet (night trains spec §7.2): the
// train, where to board and get off, what is on board, when it runs, and the
// stations. Shown when the sheet's drafted mode is night train. Copy comes
// from ../nightTrain.ts; data from the backend (LegNightTrain). The booking
// button lives at the top of the sheet, always visible.
//
// Like every section of the sheet it is collapsed by default (Paulo
// 2026-09-28). Closed, it still says the two things a traveller must not
// miss: the times, and that the train does not run on the leg's date.
// Design notes: docs/product/night-trains/README.md.

interface NightTrainSheetBlockProps {
  train: LegNightTrain;
  /** The two stop names, for "25 km from Potsdam". */
  fromCity: string;
  toCity: string;
  open: boolean;
  onToggle: () => void;
  /** The stop the train leaves from, or arrives at, is a change (0 nights):
   *  the traveller goes on from there the same day (spec §7.2, §7.4). */
  changeAtFrom?: boolean;
  changeAtTo?: boolean;
  /** Present on the owner's own, saved night-train leg: enables the one-tap
   *  date shift when the train does not run on the leg's date. */
  onShiftDate?: (date: string) => void;
  shiftBusy?: boolean;
}

const card: React.CSSProperties = { border: '1px solid rgba(20,40,30,0.06)', borderRadius: 16, overflow: 'hidden', marginTop: 11 };
const row: React.CSSProperties = { padding: '11px 14px', borderBottom: '1px solid rgba(20,40,30,0.06)' };
const lab: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#6c7872', marginBottom: 2 };
const val: React.CSSProperties = { fontSize: 14, fontWeight: 700, color: '#16241d' };
const sub: React.CSSProperties = { fontSize: 12, color: '#6c7872', marginTop: 2 };
const WARN = '#9c7b22';

export const TrustMark: React.FC<{ train: LegNightTrain; light?: boolean }> = ({ train, light }) => (
  <Tooltip content={trustNote(train)} widthClassName="w-max">
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: light ? '#b7b9e2' : '#6c7872', whiteSpace: 'nowrap' }}>
      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M20 6L9 17l-5-5" /></svg>
      Timetable checked
    </span>
  </Tooltip>
);

const NightTrainSheetBlock: React.FC<NightTrainSheetBlockProps> = ({ train, fromCity, toCity, open, onToggle, changeAtFrom, changeAtTo, onShiftDate, shiftBusy }) => {
  const [stationsOpen, setStationsOpen] = useState(false);
  const runs = runsLine(train);
  const beds = bedsList(train);
  const hedge = bedsHedge(train);
  const stations = train.stations || [];
  const boardNote = stationDistanceNote(train.boarding, fromCity);
  const getOffNote = stationDistanceNote(train.arrival, toCity);
  const shifts = onShiftDate && !train.timetableChanged ? (train.shiftOptions || []) : [];

  return (
    <div style={{ marginTop: 20 }}>
      <SectionToggle title={productLine(train)} lead="🌙" open={open} onToggle={onToggle}>
        <Tooltip content={NIGHT_TRAIN_TOOLTIP} widthClassName="w-64" bare>
          <button type="button" data-share-ok aria-label="About night trains" style={{ flex: 'none', background: 'none', border: 'none', padding: 0, font: 'inherit', color: '#aab4af', fontWeight: 700, cursor: 'help' }}>ⓘ</button>
        </Tooltip>
      </SectionToggle>
      {/* Always in view, open or closed. SectionToggle adds its own bottom
          margin when open, so this line pulls back up under the title. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '2px 10px', marginTop: open ? -7 : 4 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#16241d', fontVariantNumeric: 'tabular-nums' }}>{timesLine(train)}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <TrustMark train={train} />
          {/* The open-data licences' notice, where the timetable is shown (credits under the Terms; Paulo, 2026-10-01). */}
          <a href="/terms-and-conditions#open-data" target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#6c7872' }}>
            Open data
          </a>
        </span>
      </div>
      {!open && runs.tone === 'warn' && (
        <button
          type="button"
          data-share-ok
          onClick={onToggle}
          style={{ display: 'block', marginTop: 4, background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 12.5, fontWeight: 600, color: WARN, textAlign: 'left', cursor: 'pointer' }}
        >
          {runs.text}
        </button>
      )}

      {open && (
        <div style={card}>
          <div style={row}>
            <div style={lab}>Board at</div>
            <div style={val}>{boardLine(train)}</div>
            {boardNote && <div style={sub}>{boardNote}</div>}
            {changeAtFrom && <div style={sub}>Change in {fromCity}</div>}
          </div>
          <div style={row}>
            <div style={lab}>Get off at</div>
            <div style={val}>{getOffLine(train)}</div>
            {getOffNote && <div style={sub}>{getOffNote}</div>}
            {changeAtTo && <div style={sub}>Change in {toCity}</div>}
          </div>
          <div style={row}>
            <div style={lab}>On board</div>
            <div style={{ ...val, fontWeight: 600 }}>{beds.join(' · ')}</div>
            {hedge && <div style={sub}>{hedge}</div>}
          </div>
          <div style={row}>
            <div style={lab}>Runs</div>
            <div style={{ ...val, fontWeight: 600, color: runs.tone === 'warn' ? WARN : '#16241d' }}>{runs.text}</div>
            {shifts.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 9 }}>
                {shifts.map((o) => (
                  <button
                    key={o.date}
                    type="button"
                    onClick={() => onShiftDate && onShiftDate(o.date)}
                    disabled={shiftBusy}
                    style={{
                      font: 'inherit', fontSize: 11.5, fontWeight: 700, borderRadius: 12, padding: '8px 12px',
                      background: '#157a55', color: '#fff', border: 'none', cursor: shiftBusy ? 'default' : 'pointer', opacity: shiftBusy ? 0.6 : 1,
                    }}
                  >
                    Leave {fmtDay(o.date)}
                  </button>
                ))}
                <div style={{ ...sub, flexBasis: '100%', marginTop: 0 }}>
                  This moves a night between {fromCity} and {toCity}. Your trip keeps its length.
                </div>
              </div>
            )}
          </div>
          <div style={{ ...row, borderBottom: 'none' }}>
            <button
              type="button"
              data-share-ok
              onClick={() => setStationsOpen((o) => !o)}
              aria-expanded={stationsOpen}
              disabled={stations.length === 0}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: stations.length ? 'pointer' : 'default', textAlign: 'left' }}
            >
              <span style={val}>{stations.length} {stations.length === 1 ? 'station' : 'stations'}</span>
              {stations.length > 0 && <span aria-hidden style={{ color: '#b3bbb6', fontSize: 15, transform: stationsOpen ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>›</span>}
            </button>
            {stationsOpen && (
              <ol style={{ listStyle: 'none', margin: '9px 0 0', padding: 0, display: 'grid', gap: 5 }}>
                {stations.map((s, i) => (
                  <li key={`${s.name}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5, color: '#16241d' }}>
                    <span style={{ minWidth: 0 }}>{s.name}</span>
                    <span style={{ color: '#6c7872', flex: 'none', fontVariantNumeric: 'tabular-nums' }}>{i === 0 ? s.departure : (s.arrival || s.departure)}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NightTrainSheetBlock;
